import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, SectionCard } from "@/components/kit";
import { ConsentDocument } from "@/components/ConsentDocument";
import { ConsentActions } from "@/components/ConsentActions";
import { useSuspension } from "@/components/ConsentGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { printConsent, fmtLong } from "@/lib/consentTemplate";
import { Download } from "lucide-react";

export const STATUS_TONE = {
  Pending: "bg-slate-100 text-slate-700 border-slate-200",
  "Will Continue": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Clarification Requested": "bg-amber-100 text-amber-800 border-amber-200",
  "Wishes to Exit": "bg-red-100 text-red-700 border-red-200",
};

export default function MyConsent() {
  const qc = useQueryClient();
  const { data, isLoading } = useSuspension();
  if (isLoading) return <Skeleton className="h-96 w-full" />;
  const c = data?.consent;
  if (!c) return <PageHeader title="My Consent" subtitle="No consent document applies to your account." />;
  const canRespond = ["Pending", "Clarification Requested"].includes(c.status);
  return (
    <div className="space-y-6">
      <PageHeader title="My Consent" subtitle="Team reallocation & capped part-time working arrangements">
        <Badge variant="outline" className={STATUS_TONE[c.status]} data-testid="my-consent-status">{c.status}</Badge>
        <Button variant="outline" size="sm" onClick={() => printConsent(c)} data-testid="my-consent-download">
          <Download className="mr-2 h-4 w-4" />Download copy
        </Button>
      </PageHeader>
      {c.signed_at && (
        <p className="text-sm text-slate-600" data-testid="my-consent-signed">Signed by <b>{c.signature_name}</b> on {fmtLong(c.signed_at)}.</p>
      )}
      {c.status === "Clarification Requested" && (
        <p className="text-sm text-amber-800" data-testid="my-consent-note">Your question: “{c.note}”. The Founder will get back to you — you can still respond below.</p>
      )}
      <SectionCard title={c.doc_ref}><ConsentDocument consent={c} /></SectionCard>
      {canRespond && (
        <SectionCard title="Your decision">
          <ConsentActions onDone={() => qc.invalidateQueries({ queryKey: ["suspension-me"] })} />
        </SectionCard>
      )}
    </div>
  );
}
